import React, { useContext } from "react";

import classNames from "classnames";
import kebabCase from "lodash.kebabcase";
import { observer } from "mobx-react";
import Image from "next/legacy/image";
import Link from "next/link";
import { useParams, useSearchParams } from "../../shared/hooks/useNavigation";
import { useRouter } from "../../shared/hooks/useNavigation";
import styled from "styled-components";

import ComponentContext from "../../contexts/ComponentContext";
import { useStores } from "../../contexts/StoreContext";
import BlogSidebar from "../BlogSidebar/BlogSidebar";

const StyledCoverWrapper = styled.div`
  height: 300px;
  width: 100%;
`;

const StyledDescription = styled.p`
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const StyledLink = styled.span`
  a {
    color: ${({ theme }) => theme.textColor} !important;
  }
`;

const dateOptions = {
  day: "numeric",
  month: "long",
  year: "numeric",
};

function BlogPostList() {
  const { blogStore } = useStores();
  const { posts, categories } = blogStore;
  const router = useRouter();
  const params = useParams();
  const searchParams = useSearchParams();
  const search = Object.fromEntries(searchParams.entries());
  const combindedParms = { ...params, ...search };

  const { category } = combindedParms;

  const { enableSidebar } = useContext(ComponentContext);

  const categoryLabel = category
    ? categories.find((item) => item.slug === category)?.categoryName
    : "";

  const postListClassname = classNames("blog-post-list", {
    "col-md-9 col-lg-10": enableSidebar,
  });

  return (
    <div className="row">
      <div className={postListClassname}>
        {categoryLabel && (
          <h1 className="mb-4">Posts from {categoryLabel} category</h1>
        )}
        {posts.length === 0 && <h2 className="d-block mt-5">0 results</h2>}
        <div className="row">
          {posts.map((post) => {
            const { title, description, cover, id, publishedAt } = post;
            const coverUrl = cover?.url;
            const date = new Date(publishedAt).toLocaleString(
              router.locale,
              dateOptions
            );

            const postAuthor = post?.post_authors?.[0]?.authorName;

            const categoryData = post.post_categories?.[0];

            const postCategory = categoryData?.categoryName;
            const postCategorySlug = categoryData?.slug;

            return (
              <div key={id} className="post-wrapper mb-4 col-md-4">
                <div className="bg-white post-content">
                  {postCategory && (
                    <div className="post-category text-center py-1">
                      <span>Category: </span>
                      <StyledLink className="me-3">
                        <Link
                          href={`blog?category=${postCategorySlug}`}
                          passHref
                        >
                          {postCategory}
                        </Link>
                      </StyledLink>
                    </div>
                  )}
                  <Link
                    href={`/blog/${id}/${kebabCase(title)}`}
                    passHref
                    className="text-decoration-none text-black"
                  >
                    <StyledCoverWrapper className="position-relative">
                      <Image
                        alt={cover?.alternativeText || "Post cover"}
                        layout="fill"
                        objectFit="cover"
                        src={coverUrl}
                      />
                    </StyledCoverWrapper>
                    <div className="p-3">
                      <div className="d-flex flex-column justify-content-between h-100">
                        <div>
                          <h2>{title}</h2>
                          <div className="d-flex justify-content-between mb-3">
                            <div>
                              {postAuthor && (
                                <span className="me-5">
                                  {`Author: ${postAuthor}`}
                                </span>
                              )}
                            </div>
                            <span>{date}</span>
                          </div>
                          <StyledDescription className="">
                            {description}
                          </StyledDescription>
                          <span className="me-3 text-decoration-underline">
                            Read more
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {enableSidebar && (
        <div className="sidebar-wrapper col-md-3 col-lg-2">
          <BlogSidebar />
        </div>
      )}
    </div>
  );
}

export default observer(BlogPostList);
